import React, { useContext, useState } from 'react';
import { Context } from '../context';
import { Button, Modal, Checkbox, Select, Alert, message, Space, Tag } from 'antd';

/**
 * Bulk «Обработать → опубликовать» bar + confirm modal for the projects list.
 * Select lectures (per-row checkboxes and/or «выбрать все по фильтру») and run, behind a confirmation:
 *   Step A — mass_full_cycle (keep proofread translation → dub → video → publish R2 and/or YouTube);
 *   Step B — re-cut the lectures' cut-videos clips (RU+EN) → always R2/site.
 * Expensive + irreversible, so the modal shows the exact count + a warning and requires explicit confirm.
 */
export function BulkProcessBar() {
  const context = useContext(Context);
  const { state } = context;
  const selected = state?.selectedIds || new Set();
  const count = selected.size;
  const stageFilter = state?.stageFilter || '';
  const stageLang = state?.stageLang || 'en';

  const [selecting, setSelecting] = useState(false);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // modal step toggles
  const [processLecture, setProcessLecture] = useState(true);
  const [publishR2, setPublishR2] = useState(true);
  const [publishYoutube, setPublishYoutube] = useState(false);
  const [youtubePrivacy, setYoutubePrivacy] = useState('private');
  const [recutClips, setRecutClips] = useState(false);
  const [recutSkipEn, setRecutSkipEn] = useState(false);

  // Only show the bar when a stage filter is active or something is selected.
  if (!stageFilter && count === 0) return null;

  const onSelectAll = async () => {
    setSelecting(true);
    try {
      const total = await context.selectAllMatchingFilter();
      message.success(`Выбрано ${total} лекций по фильтру`);
    } catch (e) {
      message.error('Не удалось выбрать по фильтру');
    } finally {
      setSelecting(false);
    }
  };

  const onConfirm = async () => {
    if (!processLecture && !recutClips) {
      message.warning('Выберите хотя бы один шаг');
      return;
    }
    if (processLecture && !publishR2 && !publishYoutube) {
      message.warning('Выберите куда публиковать лекцию (R2 и/или YouTube)');
      return;
    }
    setSubmitting(true);
    try {
      const res = await context.massProcess({
        media_ids: Array.from(selected),
        lang: stageLang || 'en',
        language_from: 'ru',
        process_lecture: processLecture,
        publish_r2: publishR2,
        publish_youtube: publishYoutube,
        youtube_privacy: youtubePrivacy,
        recut_clips: recutClips,
        recut_skip_en: recutSkipEn,
      });
      const jobId = res?.mass_job?.job?.id || res?.mass_job?.job_id || null;
      const recut = res?.clip_recut?.queued || 0;
      message.success(
        `Запущено: ${processLecture ? `mass-job ${jobId || '✓'}` : ''}` +
        `${recut ? ` · перенарезка клипов: ${recut}` : ''}`.trim(),
        6,
      );
      context.clearSelection();
      setOpen(false);
    } catch (e) {
      message.error('Ошибка запуска: ' + (e?.message || 'unknown'));
    } finally {
      setSubmitting(false);
    }
  };

  const big = count > 25;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
      padding: '8px 16px', background: '#fff7e6', borderTop: '1px solid #ffe7ba',
    }}>
      <Tag color={count ? 'orange' : 'default'}>{count} выбрано</Tag>
      <Button size="small" onClick={onSelectAll} loading={selecting}>Выбрать все по фильтру</Button>
      {count > 0 && <Button size="small" onClick={() => context.clearSelection()}>Снять выделение</Button>}
      <Button
        type="primary" danger={big} size="small" disabled={count === 0}
        onClick={() => setOpen(true)}
      >
        Обработать → опубликовать ({count})
      </Button>

      <Modal
        title={`Массовая обработка: ${count} лекций (${(stageLang || 'en').toUpperCase()})`}
        open={open}
        onOk={onConfirm}
        confirmLoading={submitting}
        onCancel={() => setOpen(false)}
        okText="Запустить"
        cancelText="Отмена"
        okButtonProps={{ danger: big }}
        width={560}
      >
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message="Это дорогая и необратимая операция"
          description="Озвучка (дубляж), рендер финального видео и публикация — по каждой лекции. Проверьте количество и цель публикации перед запуском."
        />
        {big && (
          <Alert type="error" showIcon style={{ marginBottom: 12 }}
            message={`Выбрано много лекций (${count}). Убедитесь, что это намеренно.`} />
        )}

        {/* Step A — lecture */}
        <div style={{ marginBottom: 10 }}>
          <Checkbox checked={processLecture} onChange={(e) => setProcessLecture(e.target.checked)}>
            <b>Обработать и опубликовать лекцию</b> (mass full-cycle: озвучка → видео → публикация)
          </Checkbox>
          {processLecture && (
            <div style={{ marginLeft: 24, marginTop: 6 }}>
              <Space direction="vertical" size={4}>
                <Checkbox checked={publishR2} onChange={(e) => setPublishR2(e.target.checked)}>
                  R2 / сайт (torsunov.com)
                </Checkbox>
                <Space>
                  <Checkbox checked={publishYoutube} onChange={(e) => setPublishYoutube(e.target.checked)}>
                    YouTube
                  </Checkbox>
                  {publishYoutube && (
                    <Select size="small" value={youtubePrivacy} onChange={setYoutubePrivacy} style={{ width: 130 }}
                      options={[
                        { value: 'private', label: 'private' },
                        { value: 'unlisted', label: 'unlisted' },
                        { value: 'public', label: 'public' },
                      ]} />
                  )}
                </Space>
              </Space>
            </div>
          )}
        </div>

        {/* Step B — clips */}
        <div style={{ marginBottom: 6 }}>
          <Checkbox checked={recutClips} onChange={(e) => setRecutClips(e.target.checked)}>
            <b>Перенарезать клипы этих лекций</b> (RU+EN → всегда R2/сайт, не YouTube)
          </Checkbox>
          {recutClips && (
            <div style={{ marginLeft: 24, marginTop: 6 }}>
              <Checkbox checked={recutSkipEn} onChange={(e) => setRecutSkipEn(e.target.checked)}>
                только RU (пропустить EN)
              </Checkbox>
              <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
                Перезапишет ручные правки клипов; границы/ID клипов сохраняются.
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
